import * as React from 'react';

import { useQuery } from '@apollo/client';

import user from 'js/lib/user';

import type {
  CourseNavigationQuery,
  CourseNavigationQueryVariables,
} from 'bundles/course-v2/queries/courseNavigationQuery/__generated__/courseNavigationQuery';
import courseNavigationQuery from 'bundles/course-v2/queries/courseNavigationQuery/courseNavigationQuery.graphql';
import { courseNavigationV2ToCourseNavigationV1 } from 'bundles/course-v2/utils/courseNavigationV2ToCourseNavigationV1';
import type { CourseNavigationV1 } from 'bundles/naptimejs/resources/__generated__/CourseNavigationV1';

type Props = {
  courseId: string;
  children: React.ReactNode;
};

type ContextData = {
  /**
   * Combined user and course id
   */
  id?: string;

  /**
   * Course navigation items in the shape of CourseNavigationV1
   * tldr: it should be only simplified when the week -> module migration is over
   * @see bundles/course-v2/utils/courseNavigationV2ToCourseNavigationV1 for details
   */
  navigationItems?: CourseNavigationV1['navigationItems'];
  /**
   * Course navigation v2 current module
   * Note: it can be `0`. For course preview
   */
  currentModule?: number | null;

  loading: boolean;
};

const CourseNavigationContext = React.createContext<ContextData | null>(null);

const CourseNavigationProvider = ({ children, courseId }: Props) => {
  const { data, loading } = useQuery<CourseNavigationQuery, CourseNavigationQueryVariables>(courseNavigationQuery, {
    variables: { userId: String(user.get().id), courseId },
    fetchPolicy: 'cache-and-network',
    context: { clientName: 'gatewayGql' },
    skip: !courseId,
  });

  const courseNavigationResults = data?.CourseNavigation?.getCourseNavigationByUserIdCourseId;

  const navigationItems = courseNavigationResults
    ? courseNavigationV2ToCourseNavigationV1(courseNavigationResults)
    : undefined;

  return (
    <CourseNavigationContext.Provider
      value={{ ...navigationItems, currentModule: courseNavigationResults?.modules?.currentModule, loading }}
    >
      {children}
    </CourseNavigationContext.Provider>
  );
};

export const useCourseNavigationContext = () => {
  const context = React.useContext(CourseNavigationContext);
  if (!context) {
    throw new Error('useCourseNavigationContext must be used within a CourseNavigationProvider');
  }
  return context;
};

export default CourseNavigationProvider;
